import React, { useState } from 'react';
import PTR from 'react-simple-pull-to-refresh';
import { RefreshCw, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';
import appSound from '@/services/app-sound-service';

interface PullToRefreshProps {
  children: React.ReactNode;
  onRefresh: () => Promise<any>;
  className?: string;
  refreshTheme?: 'primary' | 'secondary' | 'yellow' | 'blue';
  showToastOnRefresh?: boolean;
  toastMessage?: string;
  pullDistance?: number;
  maxPullDistance?: number;
}

export function PullToRefresh({
  children,
  onRefresh,
  className = '',
  refreshTheme = 'primary', 
  showToastOnRefresh = false,
  toastMessage = 'Content refreshed',
  pullDistance = 50,
  maxPullDistance = 120
}: PullToRefreshProps) {
  const { toast } = useToast();
  const [showSuccess, setShowSuccess] = useState(false);

  // Set color theme based on the theme prop
  const getThemeColor = () => {
    switch (refreshTheme) {
      case 'yellow':
        return 'text-yellow-500';
      case 'blue':
        return 'text-blue-500';
      case 'secondary':
        return 'text-secondary';
      case 'primary':
      default:
        return 'text-primary';
    }
  };

  const handleRefresh = async () => {
    try {
      // Play sound when refreshing
      appSound.playButtonSound();
      
      // Execute the refresh action
      await onRefresh();
      
      // Show success state
      setShowSuccess(true);
      
      // Show toast if enabled
      if (showToastOnRefresh) {
        toast({
          title: "Success",
          description: toastMessage,
        });
      }
      
      // Reset success state after 1.5 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 1500);
      
      return true;
    } catch (error) {
      console.error('Refresh error:', error);
      
      // Show error toast
      toast({
        title: "Error refreshing",
        description: "Could not refresh content. Please try again.",
        variant: "destructive",
      });
      
      return false;
    }
  };

  // Content shown when pulling down
  const pullingContent = (
    <div className="flex flex-col items-center justify-center py-4">
      <div className={`relative flex items-center justify-center ${getThemeColor()}`}>
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.7, 1, 0.7]
          }}
          transition={{ 
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className={`absolute inset-0 rounded-full bg-current opacity-20`}
        />
        <RefreshCw className="h-8 w-8 animate-pulse" />
      </div>
      <p className="mt-2 text-sm text-white/70">Pull to refresh...</p>
    </div>
  );

  // Content shown when refreshing
  const refreshingContent = (
    <div className="flex flex-col items-center justify-center py-4">
      <AnimatePresence mode="wait">
        {showSuccess ? (
          <motion.div
            key="success"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`flex items-center justify-center ${getThemeColor()}`}
          >
            <div className="relative">
              <motion.div 
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.3, 0.6, 0.3]
                }}
                transition={{ duration: 1 }}
                className="absolute inset-0 bg-current rounded-full opacity-20" 
              />
              <Check className="h-8 w-8" strokeWidth={3} />
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="loading"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`relative flex items-center justify-center ${getThemeColor()}`}
          >
            <RefreshCw className="h-8 w-8 animate-spin" />
          </motion.div>
        )}
      </AnimatePresence>
      <p className="mt-2 text-sm text-white/70">
        {showSuccess ? "Refreshed!" : "Refreshing..."}
      </p>
    </div>
  );

  return (
    <PTR
      onRefresh={handleRefresh}
      pullingContent={pullingContent}
      refreshingContent={refreshingContent}
      pullDownThreshold={pullDistance}
      maxPullDownDistance={maxPullDistance}
      isPullable={true}
      resistance={3}
      className={className}
    >
      {children}
    </PTR>
  );
}